import { useContext, useRef } from 'react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@repo/videa-components/src/ui/hover-card';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import type { Appointment } from '@/react-query/useAppointments';

import { usePreferences } from '../../../../../react-query/usePreferences';
import type { TVisitSummary } from '../../../../../react-query/useVisitSummaries';
import { useUniversalToFDI } from '../../../../../utils/internationalization/useUniversalToFDI';
import { mixpanelEvents, useTrackEvent } from '../../../../../utils/mixpanel';
import { useIsBasicOdontogram } from '../hooks';
import type { TDisplayOrderQuadrantAbbreviations } from '../types';
import { getRecommendedProcedures, isLowerQuadrant } from '../utils';

import { QuadrantTxInfo } from './quadrantTxInfo';

export const QuadrantHover = ({
  activeImageId,
  appointment,
  children,
  visitSummary,
  quadrant,
  visualizationStatus,
  module,
}: {
  activeImageId?: string;
  appointment?: Appointment;
  children: React.ReactNode;
  visitSummary?: TVisitSummary;
  quadrant: TDisplayOrderQuadrantAbbreviations;
  visualizationStatus?: string;
  module?: 'image-viewer' | 'patient-list';
}) => {
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const isBasicOdontogram = useIsBasicOdontogram();
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const { displayLocalizedQuadrantLongName } = useUniversalToFDI();
  const { data: prefs } = usePreferences();
  const { trackEvent } = useTrackEvent();
  const currentQuadrant = visitSummary?.quadrants.find(
    (item) => item.quadrant === quadrant,
  );
  const hasTreatments = !!currentQuadrant?.treatments?.length;
  const recommendedProcedures = getRecommendedProcedures(
    currentQuadrant,
    prefs,
    isBasicOdontogram,
    visitSummary?.isPerioPatient,
  );
  const showQuadrantHover = hasTreatments || !!recommendedProcedures.length;

  return (
    <HoverCard
      open={showQuadrantHover ? undefined : false}
      openDelay={100}
      closeDelay={100}
      onOpenChange={(open) => {
        if (open) {
          hoverTimeoutRef.current = setTimeout(() => {
            trackEvent(mixpanelEvents.odontogramHoverMouthPart, {
              mouthPart: `quadrant-${quadrant}`,
              status: visualizationStatus,
              estimatedVisitId: visitSummary?.estimatedVisitId,
              unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
              pmsPatientId: appointment?.patient.pmsPatientId,
              pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
              imagingPatientId: appointment?.patient.imagingPatientId,
              isIframe: !!isIframe,
              externalPatientId,
              externalImageId,
              imageId: activeImageId,
              patientId: selectedPatientId ?? patientId,
              trialStatus,
              centralizedBilling: isCentralizedBilling,
            });
          }, 500);
        } else if (hoverTimeoutRef.current) {
          // Clear the timeout if the hover ends before the threshold
          clearTimeout(hoverTimeoutRef.current);
        }
      }}
    >
      <HoverCardTrigger>{children}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side={isLowerQuadrant(quadrant) ? 'bottom' : 'top'}
          className={classNames('vai-w-max vai-font-sans vai-z-[200]', {
            'vai-bg-white vai-border-slate-200 vai-text-slate-950':
              module !== 'image-viewer',
            'vai-bg-zinc-950 vai-border-zinc-800 vai-text-slate-100':
              module === 'image-viewer',
            '!vai-p-3': isIframe,
          })}
        >
          <div className="vai-flex vai-flex-col vai-gap-4">
            <QuadrantTxInfo
              module={module}
              affectedArea={displayLocalizedQuadrantLongName(quadrant)}
              procedures={currentQuadrant?.procedures}
              treatments={currentQuadrant?.treatments}
              recommendedProcedures={recommendedProcedures}
              isPerioPatient={visitSummary?.isPerioPatient}
            />
          </div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
